import React from 'react';
import { type ComponentProps } from '../../types/aha-editor';
import { useReadOnly, useSlate } from 'slate-react';
import type Tool from '../../tools/Tool';
import { ApplicationView, useController } from '@aha-app/mvc';
import AhaEditorV2Controller from '../../controllers/AhaEditorV2Controller';
import ToolButton from '../../components/Toolbar/ToolButton';
import classnames from 'classnames';
import styles from './ElementToolbar.module.css';

// Shows a toolbar on hover. This is to work around Slate not tracking
// selection and focus in read-only views, so we can't show the
// element toolbars.
//
// Since these only appear for read-only views, they should be much
// simpler -- usually just a single button, and not supporting many of
// the actions a tool can take.
const withHoverToolbar = (
  Component: React.ComponentType<any>,
  toolbarTools: (typeof Tool)[]
) => {
  const WrappedComponent = (props: ComponentProps) => {
    const controller = useController(AhaEditorV2Controller);
    const editor = useSlate();
    const readOnly = useReadOnly();
    const { element } = props;

    if (window.isKnowledgeBase || window.isIdeaPortal || window.isPublishing) {
      return <Component {...props} />;
    }

    const availableTools =
      readOnly &&
      toolbarTools.filter((tool) =>
        tool.available({ controller, editor, element })
      );

    const toolbar =
      availableTools && availableTools.length ? (
        <div
          className={classnames(
            styles.hoverToolbar,
            styles.elementToolbarCommon
          )}
        >
          {availableTools.map((tool, index) => (
            <ToolButton
              key={index}
              tool={tool}
              controller={controller}
              editor={editor}
              element={element}
            />
          ))}
        </div>
      ) : undefined;

    return (
      <div className={styles.hoverToolbarWrapper}>
        {toolbar}
        <Component {...props} />
      </div>
    );
  };

  const ObservedComponent = ApplicationView(WrappedComponent);
  return ObservedComponent;
};

export default withHoverToolbar;
